import { SignupRequest, SignupResponse, UpdateMarketingConsentRequest, SetSignupAttributionRequest } from './types';
import { BackendClient, BACKEND_URL, v1App } from '@/infrastructure/clients/BackendClient';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { captureError } from '@/utils/sentry';

export interface IAuthService {
  signUp(signupData: SignupRequest, options?: { onSlow?: () => void }): Promise<SignupResponse>;
  setSupabaseSession(accessToken: string, refreshToken: string): Promise<void>;
  updateMarketingConsent(request: UpdateMarketingConsentRequest): Promise<void>;
  setSignupAttribution(request: SetSignupAttributionRequest): Promise<void>;
  notifySlackUserSignup(): Promise<void>;
}

/** Build Sentry extras for signup failures (request, backend, browser hints). */
function getSignupSentryExtras() {
  let backendHost: string | undefined;
  try {
    backendHost = new URL(BACKEND_URL).host;
  } catch {
    backendHost = undefined;
  }
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const conn = nav ? (nav as { connection?: { effectiveType?: string; rtt?: number; downlink?: number } }).connection : undefined;
  return {
    backendHost,
    navigatorOnLine: nav?.onLine,
    connectionEffectiveType: conn?.effectiveType,
    connectionRtt: conn?.rtt,
    connectionDownlink: conn?.downlink,
    signupTimeoutMs: AuthService.SIGNUP_TIMEOUT_MS,
  };
}

export class AuthService implements IAuthService {
  static readonly SIGNUP_SLOW_MS = 20_000;
  // The server finishes a sign-up it started either way, so this is not a failure
  static readonly SIGNUP_TIMEOUT_MS = 120_000;

  /**
   * Signs up a new user (POST /api/v1/sign-ups). The tokens are null while the email awaits confirmation.
   * @returns Promise with session data or throws an error
   */
  async signUp(signupData: SignupRequest, { onSlow }: { onSlow?: () => void } = {}): Promise<SignupResponse> {
    const controller = new AbortController();
    const slowId = setTimeout(() => onSlow?.(), AuthService.SIGNUP_SLOW_MS);
    const timeoutId = setTimeout(() => controller.abort(), AuthService.SIGNUP_TIMEOUT_MS);
    try {
      const { data, error } = await toResult(v1App.POST('/api/v1/sign-ups', { body: signupData, signal: controller.signal }));
      if (!error) return data;

      // No response at all: unreachable, timed out, blocked
      if (error.status === 0) {
        const timedOut = controller.signal.aborted;
        captureError(error, {
          domain: 'auth',
          operation: 'signUp',
          errorType: timedOut ? 'timeout' : 'network',
          errorMessage: error.message,
          ...getSignupSentryExtras(),
        });
        throw new Error(timedOut
          ? "We didn't hear back in time. Your account may still have been created: try signing in with the same email and password."
          : "We couldn't reach our servers. Please try again in a moment.");
      }
      captureError(error, {
        domain: 'auth',
        operation: 'signUp',
        errorType: 'api',
        status: error.status,
        code: error.code,
        ...getSignupSentryExtras(),
      });
      // A sign_up_failed or rate-limit problem carries a title fit to show; a server error's may not
      throw new Error(error.status >= 500
        ? "We're having trouble on our side. Please try again in a moment."
        : error.message);
    } finally {
      clearTimeout(slowId);
      clearTimeout(timeoutId);
    }
  }

  /**
   * Sets the session in Supabase client after successful signup
   * This is needed to authenticate the user after the backend creates the account
   */
  async setSupabaseSession(accessToken: string, refreshToken: string): Promise<void> {
    try {
      const { error } = await BackendClient.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        throw new Error(`Failed to set session: ${error.message}`);
      }
    } catch (error) {
      captureError(error, { domain: 'auth', operation: 'setSupabaseSession' });
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to set authentication session', { cause: error });
    }
  }

  /**
   * Updates the signed-in user's marketing consent preference on the backend.
   * The backend records the consent timestamp and syncs to Customer.io.
   */
  async updateMarketingConsent(request: UpdateMarketingConsentRequest): Promise<void> {
    const { error } = await toResult(v1.PUT('/api/v1/orgs/me/member/marketing-consent', { body: request }));
    if (error) {
      captureError(error, { domain: 'auth', operation: 'updateMarketingConsent' });
      throw error;
    }
  }

  /**
   * Persists first-touch signup attribution (utm_source / utm_campaign) to the signed-in
   * user's org_member. The backend fills each column only if currently null (first-touch wins),
   * so this is safe to call on every authenticated load for both going-forward signups
   * and backfilling existing users.
   */
  async setSignupAttribution(request: SetSignupAttributionRequest): Promise<void> {
    const { error } = await toResult(v1.PUT('/api/v1/orgs/me/member/signup-attribution', { body: request }));
    if (error) {
      captureError(error, { domain: 'auth', operation: 'setSignupAttribution' });
      throw error;
    }
  }

  /**
   * Announces the signed-in user's new account in the Slack signup channel. The backend builds
   * the message from the account it holds (name, email, provider, marketing consent), so call it
   * once the session is set and any consent change has been saved.
   */
  async notifySlackUserSignup(): Promise<void> {
    const { error } = await toResult(v1.POST('/api/v1/staff-notices/sign-up'));
    if (error) console.error('Failed to send signup Slack notification:', error.status, error.code);
  }
}
